import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
} from '@aigovops/design-system';
import type { ResearchSourceKind } from '@aigovops/core';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { queryKeys } from '../lib/query-keys';
import {
  approveProposedChange,
  applyProposedChange,
  createProposedChange,
  listResearchSources,
  triggerSourceRun,
  type ResearchSourceListRow,
} from '../lib/api';
import { ProposeSourceForm } from '../components/ProposeSourceForm';

/**
 * /app/sources — editorial list of research sources.
 *
 * Complements the Editorial queue: the queue shows PROPOSALS (things
 * awaiting review); this page shows ACTIVE + DEACTIVATED sources (what
 * the scheduler is currently dispatching). Reads are open to any
 * workspace member; write actions (Propose, Deprecate, Run now) are
 * gated to admin + editor and match the API's canWrite() rule.
 *
 * "Deprecate" reuses the ADR-0002 flow — raise a research_source
 * deprecate proposal + auto-approve+apply — so every deactivation
 * lands in the proposed_changes audit trail.
 */

type StatusFilter = 'active' | 'inactive' | 'all';
type KindFilter = ResearchSourceKind | 'all';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Deactivated only' },
  { value: 'all', label: 'All sources' },
];
const KIND_OPTIONS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'Any kind' },
  { value: 'legislation', label: 'Legislation' },
  { value: 'framework', label: 'Framework' },
  { value: 'breach_intel', label: 'Breach intel' },
  { value: 'case_law', label: 'Case law' },
  { value: 'vendor_catalog', label: 'Vendor catalog' },
  { value: 'vendor_sentiment', label: 'Vendor sentiment' },
];

export function Sources() {
  const { user } = useAuth();
  const canWrite = !user || user.role === 'admin' || user.role === 'editor';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [proposeOpen, setProposeOpen] = useState(false);
  const [busySourceId, setBusySourceId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [deprecateReasonFor, setDeprecateReasonFor] = useState<string | null>(null);
  const [deprecateReason, setDeprecateReason] = useState('');

  const listQ = useQuery({
    queryKey: queryKeys.sources(statusFilter, kindFilter),
    queryFn: () =>
      listResearchSources({
        kind: kindFilter === 'all' ? undefined : (kindFilter as ResearchSourceKind),
        is_active:
          statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
      }),
  });
  const list = {
    data: listQ.data ?? null,
    error: listQ.error?.message ?? null,
    loading: listQ.isLoading,
    refetch: listQ.refetch,
  };

  const reviewerId = user?.email ?? 'unknown-reviewer';

  const runNow = useCallback(
    async (sourceId: string) => {
      setBusySourceId(sourceId);
      setActionError(null);
      try {
        await triggerSourceRun(sourceId);
        // Poll refetch after a beat so the run_count bumps visibly.
        setTimeout(() => list.refetch(), 1200);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusySourceId(null);
      }
    },
    [list],
  );

  const deprecate = useCallback(
    async (sourceId: string, reason: string) => {
      setBusySourceId(sourceId);
      setActionError(null);
      try {
        const raised = await createProposedChange({
          target_type: 'research_source',
          target_id: sourceId,
          change_type: 'deprecate',
          proposed_state: {},
          ai_rationale: reason,
        });
        const decision = { reviewer: reviewerId, review_notes: reason };
        await approveProposedChange(raised.change_id, decision);
        await applyProposedChange(raised.change_id, {});
        setDeprecateReasonFor(null);
        setDeprecateReason('');
        list.refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusySourceId(null);
      }
    },
    [reviewerId, list],
  );

  // Config editor — used today for discovery-kind meta-sources whose
  // `modes.opml.urls` and per-mode `enabled` flags editors need to
  // manage without SQL. Raises + auto-approves+applies an update
  // proposal so the audit trail lives in proposed_changes alongside
  // create/deprecate. Same three-round-trip pattern as deprecate.
  const updateConfig = useCallback(
    async (sourceId: string, newConfigJson: string) => {
      setBusySourceId(sourceId);
      setActionError(null);
      try {
        const parsed = JSON.parse(newConfigJson);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Config must be a JSON object.');
        }
        const raised = await createProposedChange({
          target_type: 'research_source',
          target_id: sourceId,
          change_type: 'update',
          proposed_state: { config: parsed },
          ai_rationale: 'Config edited via /app/sources by admin.',
        });
        const decision = { reviewer: reviewerId, review_notes: 'Config edited via admin UI.' };
        await approveProposedChange(raised.change_id, decision);
        await applyProposedChange(raised.change_id, {});
        list.refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setBusySourceId(null);
      }
    },
    [reviewerId, list],
  );

  const rows = useMemo(() => list.data?.sources ?? [], [list.data]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Research sources"
        subtitle="Feeds the scheduler dispatches. Approve new sources in the Editorial queue."
        right={
          canWrite && !proposeOpen ? (
            <Button variant="secondary" compact onClick={() => setProposeOpen(true)}>
              + Propose new source
            </Button>
          ) : null
        }
      />

      {!canWrite ? (
        <Alert variant="info">
          Read-only view — an Editor or Admin role is required to add, run, or deprecate sources.
        </Alert>
      ) : null}

      {proposeOpen ? (
        <ProposeSourceForm
          onDone={() => {
            setProposeOpen(false);
            // Newly-created source lands in the review queue; nothing to
            // refetch here yet. When approved via /app/queue, the next
            // visit refetches and shows it.
          }}
          onCancel={() => setProposeOpen(false)}
        />
      ) : null}

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-3) var(--space-4)',
          background: 'var(--surface)',
          display: 'grid',
          gap: 'var(--space-3)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          alignItems: 'end',
        }}
      >
        <Field id="src-status" label="Status">
          <Select
            id="src-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
        <Field id="src-kind" label="Kind">
          <Select
            id="src-kind"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KindFilter)}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
      </div>

      {actionError ? <Alert variant="error">{actionError}</Alert> : null}

      {list.loading ? (
        <Spinner label="Loading sources" />
      ) : list.error ? (
        <Alert variant="error">{list.error}</Alert>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No sources match this filter"
          description="Try widening the filters, or propose a new source via the button above."
        />
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {rows.map((s) => (
            <SourceRow
              key={s.source_id}
              source={s}
              canWrite={canWrite}
              busy={busySourceId === s.source_id}
              deprecateReasonOpen={deprecateReasonFor === s.source_id}
              deprecateReason={deprecateReasonFor === s.source_id ? deprecateReason : ''}
              onRunNow={() => runNow(s.source_id)}
              onOpenDeprecate={() => {
                setDeprecateReasonFor(s.source_id);
                setDeprecateReason('');
              }}
              onChangeDeprecateReason={setDeprecateReason}
              onConfirmDeprecate={() => deprecate(s.source_id, deprecateReason.trim())}
              onCancelDeprecate={() => {
                setDeprecateReasonFor(null);
                setDeprecateReason('');
              }}
              onUpdateConfig={(json) => updateConfig(s.source_id, json)}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function SourceRow(props: {
  source: ResearchSourceListRow;
  canWrite: boolean;
  busy: boolean;
  deprecateReasonOpen: boolean;
  deprecateReason: string;
  onRunNow: () => void;
  onOpenDeprecate: () => void;
  onChangeDeprecateReason: (v: string) => void;
  onConfirmDeprecate: () => void;
  onCancelDeprecate: () => void;
  onUpdateConfig: (newConfigJson: string) => Promise<void>;
}) {
  const { source: s, canWrite, busy } = props;
  const [configOpen, setConfigOpen] = useState(false);
  const [configDraft, setConfigDraft] = useState('');
  // Config editing is only exposed for discovery-kind rows today —
  // that's where OPML mode's editor-managed URL list lives and where
  // the auto-pause guardrail persists per-mode history. Other kinds
  // have static config that shouldn't be admin-edited in-app.
  const supportsConfigEdit = s.kind === 'discovery';
  const openConfig = () => {
    let pretty = s.config;
    try {
      pretty = JSON.stringify(JSON.parse(s.config), null, 2);
    } catch {
      /* leave raw string if unparseable */
    }
    setConfigDraft(pretty);
    setConfigOpen(true);
  };
  const health = s.run_count === 0
    ? { label: 'no runs yet', tone: 'muted' as const }
    : s.failure_count === 0
      ? { label: `${s.run_count} runs · all ok`, tone: 'ok' as const }
      : s.failure_count === s.run_count
        ? { label: `${s.run_count} runs · all failed`, tone: 'critical' as const }
        : { label: `${s.run_count} runs · ${s.failure_count} failed`, tone: 'warning' as const };
  const toneColor =
    health.tone === 'critical' ? 'var(--critical)'
      : health.tone === 'warning' ? 'var(--warning)'
        : health.tone === 'ok' ? 'var(--success, var(--text-muted))'
          : 'var(--text-dim)';
  return (
    <li
      style={{
        border: '1px solid var(--border)',
        borderLeftWidth: '3px',
        borderLeftColor: s.is_active ? 'var(--success, var(--border))' : 'var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
        opacity: s.is_active ? 1 : 0.72,
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: '4px', minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 'var(--font-size-3xs)',
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-dim)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {s.kind} · {s.cadence} · {s.fetch_strategy}
            </span>
            {!s.is_active ? (
              <span
                style={{
                  padding: '1px 6px',
                  borderRadius: 'var(--radius-chip)',
                  border: '1px solid var(--border)',
                  background: 'var(--surface-elevated, var(--surface))',
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                deactivated
              </span>
            ) : null}
          </div>
          <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
            {s.name}
          </div>
          <a
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-muted)',
              wordBreak: 'break-all',
            }}
          >
            {s.url}
          </a>
        </div>
        <div style={{ textAlign: 'right', display: 'grid', gap: '2px', minWidth: '160px' }}>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: toneColor, fontWeight: 500 }}>
            {health.label}
          </span>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            {s.last_run_at ? `last run ${fmtRelative(s.last_run_at)}` : 'never run'}
          </span>
          {s.approved_at ? (
            <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
              approved {fmtRelative(s.approved_at)}
            </span>
          ) : null}
        </div>
      </div>

      {s.description ? (
        <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
          {s.description}
        </p>
      ) : null}

      {canWrite && s.is_active && !props.deprecateReasonOpen && !configOpen ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <Button variant="secondary" compact disabled={busy} onClick={props.onRunNow}>
            {busy ? 'Working…' : 'Run now'}
          </Button>
          {supportsConfigEdit ? (
            <Button
              variant="ghost"
              compact
              disabled={busy}
              onClick={openConfig}
              title="Edit the meta-source's config JSON — the per-mode enabled flags, OPML URL list, quality thresholds."
            >
              Edit config
            </Button>
          ) : null}
          <Button variant="ghost" compact disabled={busy} onClick={props.onOpenDeprecate}>
            Deprecate
          </Button>
        </div>
      ) : null}

      {configOpen ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <Field id={`cfg-${s.source_id}`} label="Config JSON (edited via audit-trailed update proposal)">
            <textarea
              id={`cfg-${s.source_id}`}
              rows={12}
              value={configDraft}
              onChange={(e) => setConfigDraft(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-input, var(--radius-card))',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--font-size-2xs)',
                resize: 'vertical',
                whiteSpace: 'pre',
              }}
            />
          </Field>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              variant="primary"
              compact
              disabled={busy || !configDraft.trim()}
              onClick={async () => {
                try {
                  await props.onUpdateConfig(configDraft);
                  setConfigOpen(false);
                } catch {
                  /* actionError is surfaced in the parent Alert */
                }
              }}
            >
              {busy ? 'Working…' : 'Save config'}
            </Button>
            <Button
              variant="ghost"
              compact
              disabled={busy}
              onClick={() => {
                setConfigOpen(false);
                setConfigDraft('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {props.deprecateReasonOpen ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <Field id={`dep-${s.source_id}`} label="Why deprecate this source?">
            <textarea
              id={`dep-${s.source_id}`}
              rows={2}
              value={props.deprecateReason}
              onChange={(e) => props.onChangeDeprecateReason(e.target.value)}
              placeholder="Required — audit trail."
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-input, var(--radius-card))',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-2xs)',
                resize: 'vertical',
              }}
            />
          </Field>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              variant="primary"
              compact
              disabled={busy || !props.deprecateReason.trim()}
              onClick={props.onConfirmDeprecate}
            >
              {busy ? 'Working…' : 'Confirm deprecate'}
            </Button>
            <Button variant="ghost" compact disabled={busy} onClick={props.onCancelDeprecate}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function fmtRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
